import { createLazyModuleLoader } from './lazyModuleLoader.js';
import {
  buildViewNavigationInitContext,
  buildCategoryCollectionFiltersInitContext,
  buildBatchSelectionInitContext
} from './moduleInitContextBuilders.js';
import { ensureModuleWithInit } from './moduleRuntimeHelpers.js';

export function createShelfUiDomainRuntime(context = {}) {
  const {
    importWithChunkRecovery,
    state,
    elements,
    showToast,
    escapeHtml,
    exitSelectMode,
    syncUnifiedSearchUi,
    renderCurrentView,
    ensureThoughtsModuleReady,
    renderThoughtsView,
    ensureHighlightsModuleReady,
    renderHighlights,
    ensureBooklistModuleReady,
    renderBooklistView,
    showManageBooklistBooksModal,
    showAddBooklistModal,
    canAccessFeature,
    isAdmin,
    openMembershipCenter,
    showEditCategoryMenu,
    showAllCategoriesModal,
    showAddCategoryModal,
    renderBookshelf,
    showEditCollectionMenu,
    showAllCollectionsModal,
    showAddCollectionModal,
    sortCollections,
    openModal,
    closeModal,
    deleteBook,
    removeBooksFromAllCollections,
    removeBooksFromAllBooklists,
    updateBook,
    saveCategories,
    renderCategoryFilters,
    openBook
  } = context;

  let viewNavigationModuleApi = null;
  let viewNavigationModuleLoadingPromise = null;
  let categoryCollectionFiltersModuleApi = null;
  let categoryCollectionFiltersModuleLoadingPromise = null;
  let batchSelectionModuleApi = null;
  let batchSelectionModuleLoadingPromise = null;
  let shelfViewModeModuleApi = null;
  let shelfViewModeModuleLoadingPromise = null;
  let formatPopoverModuleApi = null;
  let formatPopoverModuleLoadingPromise = null;
  let shelfSearchModuleApi = null;
  let shelfSearchModuleLoadingPromise = null;
  let shelfDashboardModuleApi = null;
  let shelfDashboardModuleLoadingPromise = null;

  const loadViewNavigationModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./viewNavigationModule.js'),
    getApi: () => viewNavigationModuleApi,
    setApi: (mod) => {
      viewNavigationModuleApi = mod;
    },
    getLoadingPromise: () => viewNavigationModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      viewNavigationModuleLoadingPromise = promise;
    }
  });

  function ensureViewNavigationModuleReady() {
    return ensureModuleWithInit(loadViewNavigationModule, {
      isInitializedExport: 'isViewNavigationModuleInitialized',
      initExport: 'initViewNavigationModule',
      createInitContext: () => buildViewNavigationInitContext({
        state,
        elements,
        exitSelectMode,
        syncUnifiedSearchUi,
        renderCurrentView,
        ensureThoughtsModuleReady,
        renderThoughtsView,
        ensureHighlightsModuleReady,
        renderHighlights,
        ensureBooklistModuleReady,
        renderBooklistView,
        showToast,
        showManageBooklistBooksModal,
        showAddBooklistModal,
        canAccessFeature,
        isAdmin,
        openMembershipCenter
      })
    });
  }

  const loadCategoryCollectionFiltersModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./categoryCollectionFiltersModule.js'),
    getApi: () => categoryCollectionFiltersModuleApi,
    setApi: (mod) => {
      categoryCollectionFiltersModuleApi = mod;
    },
    getLoadingPromise: () => categoryCollectionFiltersModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      categoryCollectionFiltersModuleLoadingPromise = promise;
    }
  });

  function ensureCategoryCollectionFiltersModuleReady() {
    return ensureModuleWithInit(loadCategoryCollectionFiltersModule, {
      isInitializedExport: 'isCategoryCollectionFiltersModuleInitialized',
      initExport: 'initCategoryCollectionFiltersModule',
      createInitContext: () => buildCategoryCollectionFiltersInitContext({
        state,
        elements,
        showEditCategoryMenu,
        showAllCategoriesModal,
        showAddCategoryModal,
        renderBookshelf,
        escapeHtml,
        showEditCollectionMenu,
        showAllCollectionsModal,
        showAddCollectionModal,
        sortCollections
      })
    });
  }

  const loadBatchSelectionModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./batchSelectionModule.js'),
    getApi: () => batchSelectionModuleApi,
    setApi: (mod) => {
      batchSelectionModuleApi = mod;
    },
    getLoadingPromise: () => batchSelectionModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      batchSelectionModuleLoadingPromise = promise;
    }
  });

  function ensureBatchSelectionModuleReady() {
    return ensureModuleWithInit(loadBatchSelectionModule, {
      isInitializedExport: 'isBatchSelectionModuleInitialized',
      initExport: 'initBatchSelectionModule',
      createInitContext: () => buildBatchSelectionInitContext({
        state,
        elements,
        renderBookshelf,
        openModal,
        closeModal,
        showToast,
        deleteBook,
        removeBooksFromAllCollections,
        removeBooksFromAllBooklists,
        updateBook,
        saveCategories,
        renderCategoryFilters,
        escapeHtml
      })
    });
  }

  const loadShelfViewModeModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./shelfViewModeModule.js'),
    getApi: () => shelfViewModeModuleApi,
    setApi: (mod) => {
      shelfViewModeModuleApi = mod;
    },
    getLoadingPromise: () => shelfViewModeModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      shelfViewModeModuleLoadingPromise = promise;
    }
  });

  function ensureShelfViewModeModuleReady() {
    return ensureModuleWithInit(loadShelfViewModeModule, {
      isInitializedExport: 'isShelfViewModeModuleInitialized',
      initExport: 'initShelfViewModeModule',
      createInitContext: () => ({
        state,
        elements,
        renderCurrentView
      })
    });
  }

  const loadFormatPopoverModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./formatPopoverModule.js'),
    getApi: () => formatPopoverModuleApi,
    setApi: (mod) => {
      formatPopoverModuleApi = mod;
    },
    getLoadingPromise: () => formatPopoverModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      formatPopoverModuleLoadingPromise = promise;
    }
  });

  function ensureFormatPopoverModuleReady() {
    return ensureModuleWithInit(loadFormatPopoverModule, {
      isInitializedExport: 'isFormatPopoverModuleInitialized',
      initExport: 'initFormatPopoverModule',
      createInitContext: () => ({
        state,
        elements,
        renderBookshelf
      })
    });
  }

  const loadShelfSearchModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./shelfSearchModule.js'),
    getApi: () => shelfSearchModuleApi,
    setApi: (mod) => {
      shelfSearchModuleApi = mod;
    },
    getLoadingPromise: () => shelfSearchModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      shelfSearchModuleLoadingPromise = promise;
    }
  });

  function ensureShelfSearchModuleReady() {
    return ensureModuleWithInit(loadShelfSearchModule, {
      isInitializedExport: 'isShelfSearchModuleInitialized',
      initExport: 'initShelfSearchModule',
      createInitContext: () => ({
        state,
        elements,
        syncUnifiedSearchUi,
        renderBooklistView,
        renderBookshelf
      })
    });
  }

  const loadShelfDashboardModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./shelfDashboardModule.js'),
    getApi: () => shelfDashboardModuleApi,
    setApi: (mod) => {
      shelfDashboardModuleApi = mod;
    },
    getLoadingPromise: () => shelfDashboardModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      shelfDashboardModuleLoadingPromise = promise;
    }
  });

  function ensureShelfDashboardModuleReady() {
    return ensureModuleWithInit(loadShelfDashboardModule, {
      isInitializedExport: 'isShelfDashboardModuleInitialized',
      initExport: 'initShelfDashboardModule',
      createInitContext: () => ({
        state,
        elements,
        openBook
      })
    });
  }

  return {
    ensureViewNavigationModuleReady,
    getViewNavigationModuleApi: () => viewNavigationModuleApi,
    ensureCategoryCollectionFiltersModuleReady,
    getCategoryCollectionFiltersModuleApi: () => categoryCollectionFiltersModuleApi,
    ensureBatchSelectionModuleReady,
    getBatchSelectionModuleApi: () => batchSelectionModuleApi,
    ensureShelfViewModeModuleReady,
    getShelfViewModeModuleApi: () => shelfViewModeModuleApi,
    ensureFormatPopoverModuleReady,
    getFormatPopoverModuleApi: () => formatPopoverModuleApi,
    ensureShelfSearchModuleReady,
    getShelfSearchModuleApi: () => shelfSearchModuleApi,
    ensureShelfDashboardModuleReady,
    getShelfDashboardModuleApi: () => shelfDashboardModuleApi
  };
}
